import { Player } from "../core/Player.js";
import { Calc } from "../math/Calc.js";
import { event } from "../util/EventUtil.js";
import { object } from "../util/ObjectUtil.js";
import { PointerDetector } from "../util/PointerDetector.js";
import { FullscreenUtil } from "./FullscreenUtil.js";

type TooltipLayout = "top" | "left" | "right" | "bottom";

export interface TooltipOptions {
    attribute?: string;
    scope?: HTMLElement | Document;
    className?: string;
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
    touchSupport?: boolean;
    fadeIn?: boolean;
    useFocusinVisible?: boolean;
}
export class Tooltip {
    private _element: HTMLDivElement;
    private _content: HTMLDivElement;
    private _corner: HTMLDivElement;
    private _target: HTMLElement = null;
    private _clientX: number = 0;
    private _clientY: number = 0;
    private _open: boolean = false;
    private _lastUpdate: number = 0;
    private _lastTouch: number = 0;
    private _margin: number = 10;
    private _offset: number = 12;
    private _lastFocus: number = 0;
    private _attribute: string = "data-tooltip";

    private _top: number = 0;
    private _right: number = 0;
    private _bottom: number = 0;
    private _left: number = 0;

    private _currentBoundsTooltip: DOMRect = null;
    private _currentText: string = null;
    private _currentBoundsTarget: DOMRect = null;
    private _state: "focus" | "hover" | "touch" | "click" = null;

    private _touchSupport: boolean = true;

    public constructor(options: TooltipOptions = null) {
        if (!document.body) {
            document.addEventListener("DOMContentLoaded",
                () => {
                    this.init(options);
                }
            );
        } else {
            this.init(options);
        }
    }

    private init(options: TooltipOptions = null): void {
        options = object.merge(
            {
                attribute: "data-tooltip",
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
                scope: document.body,
                fadeIn: true,
                useFocusinVisible: true
            },
            options
        );

        this._attribute = options.attribute;
        this._top = options.top;
        this._right = options.right;
        this._bottom = options.bottom;
        this._left = options.left;
        this._touchSupport = options.touchSupport === undefined ? true : options.touchSupport;

        this._element = document.createElement("div");
        this._element.setAttribute("data-layout", "bottom");
        this._element.classList.add("e5-tooltip", "e5-hidden");
        this._element.classList.toggle("e5-tooltip--fade-in", options.fadeIn);
        if (options.className) {
            this._element.classList.add(options.className);
        }
        this._element.innerHTML = `<div class="e5-tooltip__content"></div><div class="e5-tooltip__corner"></div>`;

        const addToContainer = () => {
            if (FullscreenUtil.element) {
                FullscreenUtil.element.appendChild(this._element);
            } else {
                document.body.appendChild(this._element);
            }
            if (this._target) {
                this.update(this._target);
            }
        };
        FullscreenUtil.onChange.add(addToContainer);
        addToContainer();

        this._content = this._element.querySelector(".e5-tooltip__content");
        this._corner = this._element.querySelector(".e5-tooltip__corner");

        const scope = options.scope;

        event.on(
            {
                type: "touchstart",
                scope: scope,
                selector: `[${this._attribute}]`,
                listener: (e) => {
                    if (!e.event.touches) {
                        return;
                    }
                    this._clientX = e.event.touches[0].clientX;
                    this._clientY = e.event.touches[0].clientY;
                    this._lastTouch = Date.now();
                    this._state = "touch";
                    this.update(e.target as HTMLElement);
                }
            }
        );

        event.on(
            {
                type: "focusin",
                scope: scope,
                selector: `[${this._attribute}]`,
                listener: (e) => {
                    this._lastFocus = Date.now();
                    this._state = "focus";

                    if (options.useFocusinVisible
                        && !e.target.matches(":focus-visible")) {
                        return;
                    }

                    this.update(e.target as HTMLElement);
                }
            }
        );

        event.on(
            {
                type: "click",
                scope: scope,
                selector: `[${this._attribute}]`,
                listener: (e) => {
                    if (this._target) {
                        return;
                    }

                    this._state = "click";
                    this.update(e.target as HTMLElement);
                }
            }
        );

        event.on(
            {
                type: "mouseenter",
                scope: scope,
                selector: `[${this._attribute}]`,
                listener: (e) => {
                    this._state = "hover";
                    this.update(e.target as HTMLElement);
                }
            }
        );

        event.on(
            {
                type: "mouseleave",
                scope: scope,
                selector: `[${this._attribute}]`,
                listener: (e) => {
                    var t = e.target as HTMLElement;
                    if (this._state == "hover" || this._target == t) {
                        this.hide();
                    }
                }
            }
        );

        event.on(
            {
                type: "mousemove",
                scope: scope,
                selector: `[${this._attribute}]`,
                listener: (e) => {
                    this._clientX = e.event.clientX;
                    this._clientY = e.event.clientY;
                    if (!this.open) {
                        return;
                    }
                    this.check(e.event.clientX, e.event.clientY);
                }
            }
        );

        Player.onTick.add(
            () => {
                if (!this.open) {
                    return;
                }
                this.check(this._clientX, this._clientY);
            }
        );

        document.addEventListener(
            "visibilitychange",
            (e) => {
                if (document.visibilityState === "hidden") {
                    this.hide();
                }
            }
        );

        window.addEventListener(
            "blur",
            (e) => {
                this.hide();
            }
        );
    }

    private update(target: HTMLElement): void {

        if (!this._touchSupport) {
            if (PointerDetector.get().isTouch) {
                this.hide();
                return;
            }
        }

        var text = target.getAttribute(this._attribute);
        if (!text) {
            this.hide();
            return;
        }

        this._lastUpdate = Date.now();

        this._currentText = text;

        this._target = target;
        this._corner.classList.add("e5-hidden");
        this._open = true;
        this._element.classList.remove("e5-hidden");

        this._content.innerHTML = text;

        this._element.style.left = "";
        this._element.style.top = "";

        var boundsTooltip = this._element.getBoundingClientRect();
        this._currentBoundsTooltip = boundsTooltip;
        var boundsTarget = target.getBoundingClientRect();
        this._currentBoundsTarget = boundsTarget;

        var layoutStrings: string = target.getAttribute("data-tooltiplayout");
        if (!layoutStrings || layoutStrings == "auto") {
            layoutStrings = "top bottom right left";
        } else if (layoutStrings == "right") {
            layoutStrings = "right left";
        } else if (layoutStrings == "left") {
            layoutStrings = "left right";
        } else if (layoutStrings == "top") {
            layoutStrings = "top bottom";
        } else if (layoutStrings == "bottom") {
            layoutStrings = "bottom top";
        }

        var layouts: TooltipLayout[] = layoutStrings.trim().split(" ") as TooltipLayout[];

        this._corner.style.top = "";
        this._corner.style.right = "";
        this._corner.style.bottom = "";
        this._corner.style.left = "";

        var x = 0;
        var y = 0;

        var top = this._top;
        var left = this._left;
        var width = window.innerWidth - this._right;
        var height = window.innerHeight - this._bottom;

        var hasFit = false;
        for (var layout of layouts) {
            if (layout === "bottom" || layout === "top") {
                x = Calc.clamp(
                    boundsTarget.width * 0.5 + boundsTarget.left - boundsTooltip.width * 0.5,
                    this._margin,
                    width - (boundsTooltip.width + this._margin));
                y = layout === "bottom" ? boundsTarget.bottom + this._offset : boundsTarget.top - (this._offset + boundsTooltip.height);
            } else if (layout === "right" || layout === "left") {
                x = layout === "right" ? boundsTarget.right + this._offset : boundsTarget.left - (this._offset + boundsTooltip.width);
                y = Calc.clamp(
                    boundsTarget.height * 0.5 + boundsTarget.top - boundsTooltip.height * 0.5,
                    this._margin,
                    height - (boundsTooltip.height + this._margin));
            }

            if ((x > left && (x + boundsTooltip.width) < width)
                && (y > top && (y + boundsTooltip.height) < height)) {
                hasFit = true;
                break;
            }
        }

        if (layout === "bottom" || layout === "top") {
            this._corner.style.left = Math.round(Calc.clamp(
                (boundsTarget.width * 0.5 + boundsTarget.left) - x,
                6,
                boundsTooltip.width - 6)) + "px";
        } else if (layout === "right" || layout === "left") {
            this._corner.style.top = Math.round(Calc.clamp(
                (boundsTarget.height * 0.5 + boundsTarget.top) - y,
                6,
                boundsTooltip.height - 6)) + "px";
        }

        this._element.setAttribute("data-layout", layout);
        this._corner.classList.remove("e5-hidden");
        this._element.style.left = Math.round(x) + "px";
        this._element.style.top = Math.round(y) + "px";
    }

    private check(clientX: number, clientY: number): void {

        if (!this._touchSupport) {
            if (PointerDetector.get().isTouch) {
                this.hide();
                return;
            }
        }

        if (!this._target || !this._target.parentElement) {
            this.hide();
            return;
        }

        if (this._state == "touch") {
            // let duration = Date.now() - this._lastTouch;
            let duration = Date.now() - this._lastUpdate;
            if (duration > 3000) {
                this.hide();
                return;
            }
        }
        else if (this._state == "focus") {
            if (document.activeElement != this._target) {
                this.hide();
                return;
            }

            // let duration = Date.now() - this._lastFocus;
            let duration = Date.now() - this._lastUpdate;
            if (duration > 3000) {
                this.hide();
                return;
            }
        }
        else if (this._state == "hover") {
            let el = document.elementFromPoint(clientX, clientY);
            if (this._target != el && !this._target.contains(el)) {
                this.hide();
                return;
            }
        }

        let needsUpdate = false;
        if (!needsUpdate && this._currentText != this._target.getAttribute(this._attribute)) {
            needsUpdate = true;
        }
        else if (!this.rectEquals(
            this._target.getBoundingClientRect(), this._currentBoundsTarget)) {

            if (this._state == "touch") {
                this.hide();
                return;
            }

            needsUpdate = true;
        }
        else if (!this.rectEqualSize(
            this._element.getBoundingClientRect(), this._currentBoundsTooltip)) {
            needsUpdate = true;
        }
        else if (this._state != "hover") {
            const targetBounds = this._currentBoundsTarget;
            let parent = this._target.parentElement;
            while (parent) {
                if (window.getComputedStyle(parent).overflow === "visible") {
                } else {
                    let boundsParent = parent.getBoundingClientRect();
                    if (!this.rectContains(boundsParent, targetBounds)) {
                        this.hide();
                        return;
                    }
                }
                parent = parent.parentElement;
            }
        }

        if (needsUpdate) {
            this.update(this._target);
        }
    }

    private rectContains(a: DOMRect, b: DOMRect): boolean {
        if (a && !b || !a && b) {
            return false;
        }
        return b.left >= a.left
            && b.left <= a.right
            && b.top >= a.top
            && b.top <= a.bottom;
    }

    private rectEqualSize(a: DOMRect, b: DOMRect): boolean {
        if (a && !b || !a && b) {
            return false;
        }
        return a.height == b.height
            && a.width == b.width;
    }

    private rectEquals(a: DOMRect, b: DOMRect): boolean {
        if (a && !b || !a && b) {
            return false;
        }
        return a.top == b.top
            && a.right == b.right
            && a.bottom == b.bottom
            && a.left == b.left;
    }

    private get open(): boolean {
        return this._open;
    }

    private hide(): void {
        if (!this._target) {
            return;
        }
        this._currentText = null;
        this._currentBoundsTarget = null;
        this._open = false;
        this._element.classList.add("e5-hidden");
        this._target = null;
    }
}